import React from 'react';
import { motion } from 'framer-motion';

const Terms: React.FC = () => {
  // Last updated date
  const lastUpdated = "November 20, 2023";
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };
  
  return (
    <div className="bg-gray-50 pt-16 pb-20 mt-14">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white rounded-lg shadow-lg overflow-hidden"
        >
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-secondary to-primary py-8 px-6">
            <motion.h1 
              variants={itemVariants}
              className="text-3xl md:text-4xl font-bold text-white"
            >
              Terms and Conditions
            </motion.h1>
            <motion.p 
              variants={itemVariants}
              className="text-white/80 mt-2"
            >
              Last updated: {lastUpdated}
            </motion.p>
          </div>
          
          {/* Content */}
          <div className="p-6 md:p-8 space-y-8">
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">1. Introduction</h2>
              <p className="text-gray-600 leading-relaxed">
                Welcome to Recall Nepal. These Terms of Service ("Terms") govern your access to and use of the Recall Nepal website, services, and applications (collectively, the "Services"). Please read these Terms carefully before using our Services.
              </p>
              <p className="text-gray-600 mt-3 leading-relaxed">
                By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">2. Services Description</h2>
              <p className="text-gray-600 leading-relaxed">
                Recall Nepal provides educational consulting services, primarily focused on helping students pursue educational opportunities in Japan. Our services include, but are not limited to, educational counseling, application assistance, language training, and cultural orientation.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">3. User Accounts</h2>
              <p className="text-gray-600 leading-relaxed">
                When you create an account with us, you must provide accurate, complete, and current information. You are responsible for safeguarding your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized access to or use of your account.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">4. Fees and Payments</h2>
              <p className="text-gray-600 leading-relaxed">
                Certain aspects of our Services may be subject to fees, which will be clearly communicated to you before you incur any charges. By agreeing to pay for premium services, you authorize us to charge the payment method you provide.
              </p>
              <p className="text-gray-600 mt-3 leading-relaxed">
                All fees are exclusive of taxes, which you will be responsible for paying. Payment obligations are non-cancelable, and fees paid are non-refundable except as expressly provided in these Terms or where required by law.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">5. User Content</h2>
              <p className="text-gray-600 leading-relaxed">
                Our Services may allow you to submit content, including personal information, application materials, and educational documents. You retain ownership of any intellectual property rights you hold in that content, but you grant us a worldwide, royalty-free license to use, copy, modify, create derivative works from, distribute, and display that content for the purpose of providing our Services.
              </p>
              <p className="text-gray-600 mt-3 leading-relaxed">
                You represent and warrant that you own or have the necessary rights to submit your content, and that your content does not violate the rights of any third party or any applicable law.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">6. Prohibited Conduct</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                You agree not to engage in any of the following prohibited activities:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Violating any applicable law, contract, intellectual property right, or other third-party right</li>
                <li>Providing false, inaccurate, or misleading information</li>
                <li>Interfering with, disrupting, or creating an undue burden on servers or networks connected to our Services</li>
                <li>Attempting to gain unauthorized access to our Services or other user accounts</li>
                <li>Spreading viruses, malware, or other malicious code</li>
                <li>Using our Services for any illegal or unauthorized purpose</li>
              </ul>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">7. Limitation of Liability</h2>
              <p className="text-gray-600 leading-relaxed">
                To the maximum extent permitted by law, Recall Nepal shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your access to or use of or inability to access or use the Services.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">8. Indemnification</h2>
              <p className="text-gray-600 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Recall Nepal and its officers, directors, employees, agents, from and against any claims, liabilities, damages, losses, and expenses, including, without limitation, reasonable legal and accounting fees, arising out of or in any way connected with your access to or use of the Services, or your violation of these Terms.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">9. Changes to Terms</h2>
              <p className="text-gray-600 leading-relaxed">
                We may revise these Terms from time to time. If we make material changes to these Terms, we will notify you by email or through the Services. Your continued use of the Services after the changes become effective constitutes your acceptance of the revised Terms.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">10. Governing Law</h2>
              <p className="text-gray-600 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of Nepal, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the Services shall be subject to the exclusive jurisdiction of the courts of Nepal.
              </p>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <h2 className="text-xl font-semibold text-gray-800 mb-3">11. Contact Information</h2>
              <p className="text-gray-600 leading-relaxed">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
                <p className="text-gray-800 font-medium">Recall Nepal</p>
                <p className="text-gray-600">Kathmandu, Nepal</p>
                <p className="text-gray-600">Email: legal@recallnepal.com</p>
                <p className="text-gray-600">Phone: +977-1-XXXXXXX</p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Terms; 